// src/hooks/useMessages.js
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

// =====================================================
// CONVERSATIONS QUERIES
// =====================================================

/**
 * Get conversations for a parent (by their children)
 * Uses optimized RPC function to bypass slow RLS policies
 */
export const useParentConversations = (parentId, childIds = [], options = {}) => {
  return useQuery({
    queryKey: ['conversations', 'parent', parentId],
    queryFn: async () => {
      if (!childIds || childIds.length === 0) return [];

      const { data, error } = await supabase.rpc('get_parent_conversations', {
        p_child_ids: childIds
      });

      if (error) throw error;

      // Transform flat response to nested format expected by UI
      return (data || []).map(row => ({
        id: row.id,
        child_id: row.child_id,
        parent_id: row.parent_id,
        group_id: row.group_id,
        status: row.status,
        last_message_at: row.last_message_at,
        created_at: row.created_at,
        child: {
          id: row.child_id,
          first_name: row.child_first_name,
          last_name: row.child_last_name,
          photo_url: row.child_photo_url,
          group_id: row.child_group_id
        },
        group: {
          id: row.group_id,
          name: row.group_name,
          color_scheme: row.group_color_scheme,
          main_teacher: row.main_teacher_id ? {
            id: row.main_teacher_id,
            first_name: row.main_teacher_first_name,
            last_name: row.main_teacher_last_name,
            avatar_url: row.main_teacher_avatar_url
          } : null
        }
      }));
    },
    enabled: !!parentId && childIds.length > 0,
    ...options
  });
};

/**
 * Get conversations for a teacher (by their groups)
 * Uses optimized RPC function to bypass slow RLS policies
 */
export const useTeacherConversations = (teacherId, groupIds = [], options = {}) => {
  return useQuery({
    queryKey: ['conversations', 'teacher', teacherId],
    queryFn: async () => {
      if (!groupIds || groupIds.length === 0) return [];

      const { data, error } = await supabase.rpc('get_teacher_conversations', {
        p_group_ids: groupIds
      });

      if (error) throw error;

      // Transform flat response to nested format expected by UI
      return (data || []).map(row => ({
        id: row.id,
        child_id: row.child_id,
        parent_id: row.parent_id,
        group_id: row.group_id,
        status: row.status,
        last_message_at: row.last_message_at,
        created_at: row.created_at,
        child: {
          id: row.child_id,
          first_name: row.child_first_name,
          last_name: row.child_last_name,
          photo_url: row.child_photo_url,
          group_id: row.child_group_id
        },
        group: {
          id: row.group_id,
          name: row.group_name,
          color_scheme: row.group_color_scheme
        },
        parent: row.parent_first_name ? {
          id: row.parent_id,
          first_name: row.parent_first_name,
          last_name: row.parent_last_name,
          avatar_url: row.parent_avatar_url
        } : null
      }));
    },
    enabled: !!teacherId && groupIds.length > 0,
    ...options
  });
};

/**
 * Get single conversation with latest messages
 * Uses optimized RPC function to bypass slow RLS policies
 */
export const useConversation = (conversationId, options = {}) => {
  return useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_conversation_details', {
        p_conversation_id: conversationId
      });

      if (error) throw error;
      if (!data || data.length === 0) return null;

      // Transform flat response to nested format expected by UI
      const row = data[0];
      return {
        id: row.id,
        child_id: row.child_id,
        parent_id: row.parent_id,
        group_id: row.group_id,
        status: row.status,
        last_message_at: row.last_message_at,
        created_at: row.created_at,
        child: {
          id: row.child_id,
          first_name: row.child_first_name,
          last_name: row.child_last_name,
          photo_url: row.child_photo_url
        },
        group: {
          id: row.group_id,
          name: row.group_name,
          color_scheme: row.group_color_scheme
        }
      };
    },
    enabled: !!conversationId,
    ...options
  });
};

// =====================================================
// MESSAGES QUERIES
// =====================================================

/**
 * Get messages for a conversation with pagination
 * Loads 10 messages at a time, ordered by newest first
 * Uses optimized RPC function to bypass slow RLS policies
 */
export const useConversationMessages = (conversationId, options = {}) => {
  return useInfiniteQuery({
    queryKey: ['messages', conversationId],
    queryFn: async ({ pageParam = 0 }) => {
      const limit = 10;
      const { data, error } = await supabase.rpc('get_conversation_messages', {
        p_conversation_id: conversationId,
        p_offset: pageParam,
        p_limit: limit
      });

      if (error) throw error;

      // Transform flat response to nested format expected by UI
      const messages = (data || []).map(row => ({
        id: row.id,
        conversation_id: row.conversation_id,
        sender_id: row.sender_id,
        content: row.content,
        read_by: row.read_by,
        created_at: row.created_at,
        sender: {
          id: row.sender_id,
          first_name: row.sender_first_name,
          last_name: row.sender_last_name,
          avatar_url: row.sender_avatar_url,
          primary_role: row.sender_primary_role,
          active_role: row.sender_active_role ?? row.sender_primary_role
        }
      }));

      const totalCount = data?.[0]?.total_count || 0;

      return {
        messages,
        nextPage: pageParam + limit < totalCount ? pageParam + limit : undefined,
        totalCount
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!conversationId,
    ...options
  });
};

/**
 * Get unread message count for a user across all conversations
 * Uses optimized database function with SECURITY DEFINER
 */
export const useUnreadMessageCount = (userId, childIds = [], groupIds = [], options = {}) => {
  return useQuery({
    queryKey: ['messages', 'unread', userId],
    queryFn: async () => {
      // Use optimized RPC function that bypasses RLS for performance
      const { data, error } = await supabase.rpc('get_unread_message_counts', {
        p_user_id: userId,
        p_child_ids: childIds.length > 0 ? childIds : null,
        p_group_ids: groupIds.length > 0 ? groupIds : null
      });

      if (error) {
        logger.error('Error fetching unread counts', { message: error.message });
        throw error;
      }

      // Function returns { total_unread, by_conversation }
      return data?.[0]?.total_unread || 0;
    },
    enabled: !!userId && (childIds.length > 0 || groupIds.length > 0),
    ...options
  });
};

/**
 * Get unread count per conversation
 * Uses optimized database function with SECURITY DEFINER
 */
export const useConversationUnreadCounts = (userId, conversationIds = [], childIds = [], groupIds = [], options = {}) => {
  return useQuery({
    queryKey: ['messages', 'unread', 'byConversation', userId, conversationIds],
    queryFn: async () => {
      if (conversationIds.length === 0) return {};

      // Use optimized RPC function that bypasses RLS for performance
      const { data, error } = await supabase.rpc('get_unread_message_counts', {
        p_user_id: userId,
        p_child_ids: childIds.length > 0 ? childIds : null,
        p_group_ids: groupIds.length > 0 ? groupIds : null
      });

      if (error) {
        logger.error('Error fetching conversation unread counts', { message: error.message });
        throw error;
      }

      // Function returns { total_unread, by_conversation }
      // by_conversation is a JSONB object like {"uuid1": 3, "uuid2": 1}
      return data?.[0]?.by_conversation || {};
    },
    enabled: !!userId && conversationIds.length > 0,
    ...options
  });
};

// =====================================================
// MUTATIONS
// =====================================================

/**
 * Create a new conversation
 */
export const useCreateConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (conversationData) => {
      const { data, error } = await supabase
        .from('conversations')
        .insert(conversationData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (error) => {
      logger.error('Create conversation error', { message: error.message });
      toast.error(`Nie udało się utworzyć rozmowy: ${error.message}`);
    }
  });
};

/**
 * Send a new message
 * Uses SECURITY DEFINER function to bypass slow RLS policies
 */
export const useSendMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ conversationId, content }) => {
      // Use RPC function that bypasses RLS but does its own access check
      const { data: messageId, error } = await supabase.rpc('send_conversation_message', {
        p_conversation_id: conversationId,
        p_content: content
      });

      if (error) throw error;

      // NOTE: Message notifications are handled by database trigger (trg_message_notify)
      // No need to create notifications here - the trigger handles it automatically

      return { id: messageId };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['messages', variables.conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (error) => {
      logger.error('Send message error', { message: error.message });
      toast.error(`Nie udało się wysłać wiadomości: ${error.message}`);
    }
  });
};

/**
 * Mark messages as read
 * Uses a database function with SECURITY DEFINER to bypass RLS
 */
export const useMarkMessagesAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ conversationId, userId }) => {
      // Call the database function that bypasses RLS
      const { data, error } = await supabase.rpc('mark_conversation_messages_read', {
        p_conversation_id: conversationId,
        p_user_id: userId
      });

      if (error) throw error;
      return { success: data };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['messages', variables.conversationId] });
      queryClient.invalidateQueries({ queryKey: ['messages', 'unread'] });
    },
    onError: (error) => {
      logger.error('Mark messages read error', { message: error.message });
    }
  });
};

/**
 * Archive a conversation
 */
export const useArchiveConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (conversationId) => {
      const { error } = await supabase
        .from('conversations')
        .update({ status: 'archived' })
        .eq('id', conversationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast.success('Rozmowa została zarchiwizowana');
    },
    onError: (error) => {
      logger.error('Archive conversation error', { message: error.message });
      toast.error(`Nie udało się zarchiwizować rozmowy: ${error.message}`);
    }
  });
};

/**
 * Delete a conversation (soft delete)
 * Uses SECURITY DEFINER function - both teachers and parents can delete
 */
export const useDeleteConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (conversationId) => {
      const { data, error } = await supabase.rpc('delete_conversation', {
        p_conversation_id: conversationId
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      toast.success('Rozmowa została usunięta');
    },
    onError: (error) => {
      logger.error('Delete conversation error', { message: error.message });
      toast.error(`Nie udało się usunąć rozmowy: ${error.message}`);
    }
  });
};
